import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Diagnostic, Programme, QuantityDef, Rate, RateRole,
} from '@tariff/authoring';
import type { Period } from '@tariff/periods';
import { displayRole, rolesUnstated } from '@tariff/roles';
import { useT, type MessageKey, type TFunction } from '../lib/i18n';
import { RateShape } from './RateShape';
import { money } from '../lib/format';

/** The compiler's three severities, which are a closed set. */
const SEVERITY: Record<string, MessageKey> = {
  error: 'programme.severityError',
  warning: 'programme.severityWarning',
  info: 'programme.severityInfo',
};

export function Diagnostics({ diagnostics }: { diagnostics: Diagnostic[] }) {
  const t = useT();
  if (diagnostics.length === 0) {
    return (
      <div className="clean">
        <span aria-hidden="true">✓</span>
        {t('programme.noDiagnostics')}
      </div>
    );
  }
  return (
    <div className="diag">
      {diagnostics.map((d, i) => (
        <div className={`dg ${d.severity}`} key={`${d.code}-${d.path}-${i}`}>
          <span className="ic" aria-hidden="true">
            {d.severity === 'error' ? '✕' : d.severity === 'warning' ? '⚠' : 'i'}
          </span>
          <span>
            <strong>{t(SEVERITY[d.severity] ?? 'programme.severityInfo')}</strong>
            {' — '}
            {/* The message itself is written by the compiler, one language deep
                in a package this app does not own. Shown as it arrives. */}
            {d.message}
            <span className="pt mono">{d.path}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function taperLabel(t: TFunction, r: Rate): string | null {
  const taper = r.extend?.taper;
  if (!taper || taper.mode === 'none') return null;
  return taper.mode === 'wholeStay'
    ? t('programme.taperWholeStay')
    : t('programme.taperPerNight');
}

/**
 * What a rate IS, in the operator's terms rather than the format's.
 *
 * ---------------------------------------------------------------------------
 * WHY GROUP AT ALL
 * ---------------------------------------------------------------------------
 *
 * A lifted legacy card is twenty-three rows in one flat list: "Week tarief A",
 * "Week tarief B", "Lange laagseizoen tarief C", "Meivakantie dag tarief". That
 * is the shape the old format stored, and the old format had one concept — a
 * priced block of nights — that it made mean four different things by convention.
 * Reading it back as a list makes the operator do that decoding.
 *
 * They do not think in blocks. They think:
 *
 *   what does a night cost, through the year
 *   what is a week worth, or a fortnight
 *   what are the named packages — Easter, Ascension, the May holiday
 *   and what else is there
 *
 * Those four ARE the model — `role` already carries them — so the grouping costs
 * nothing to compute and turns a list into an answer.
 */
const GROUPS: ReadonlyArray<{ role: string; heading: MessageKey; note: MessageKey }> = [
  { role: 'nightly', heading: 'programme.groupNightly', note: 'programme.groupNightlyNote' },
  { role: 'ladder', heading: 'programme.groupLadder', note: 'programme.groupLadderNote' },
  { role: 'arrangement', heading: 'programme.groupArrangement', note: 'programme.groupArrangementNote' },
  { role: 'other', heading: 'programme.groupOther', note: 'programme.groupOtherNote' },
];

export function RateList({ programme, diagnostics, selected, onSelect, onAdd }: {
  programme: Programme;
  diagnostics: Diagnostic[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  /** Absent on a published card, which is read-only. */
  onAdd?: (role: RateRole) => void;
}) {
  const t = useT();
  /**
   * Filter by name, id or length.
   *
   * A camping card carries thirty-five rules and a converted one over a
   * hundred, grouped into five headings — so "where is the September weekend
   * price" is a scroll and a squint. Matching the ID as well as the label
   * matters on a lifted card, where the label is what the old system called it
   * and the id is what a diagnostic names.
   */
  const [find, setFind] = useState('');
  const inert = useMemo(() => {
    const s = new Set<number>();
    for (const d of diagnostics) {
      const m = /^rates\[(\d+)\]/.exec(d.path);
      if (m && d.code === 'taper-inert') s.add(Number(m[1]));
    }
    return s;
  }, [diagnostics]);

  /** Rates by group, keeping each group's original order. */
  const grouped = useMemo(() => {
    const infer = rolesUnstated(programme);
    const needle = find.trim().toLowerCase();
    const byRole = new Map<string, Array<{ rate: Rate; index: number }>>();
    programme.rates.forEach((rate, index) => {
      if (needle !== '') {
        const hay = `${rate.label ?? ''} ${rate.id} ${rate.nights}n `
          + `${rate.active.from} ${rate.active.until}`;
        if (!hay.toLowerCase().includes(needle)) return;
      }
      // Anything with no role, or one this screen does not know, belongs in
      // "everything else" rather than vanishing.
      // A lifted card states no roles at all, and filing all twelve of its
      // rates under "everything else" is how the grouping fails on exactly the
      // cards it was written to make readable. Inferred for the reader only —
      // never written back, see `roles.ts`.
      const shown = infer ? displayRole(rate) : rate.role;
      const role = GROUPS.some((g) => g.role === shown) ? shown! : 'other';
      byRole.set(role, [...(byRole.get(role) ?? []), { rate, index }]);
    });
    return byRole;
  }, [programme, find]);

  return (
    <div className="rates">
      <div className="row">
        <label className="grow">
          {t('programme.find')}
          <input
            type="search"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder={t('programme.findPlaceholder')}
          />
        </label>
      </div>
      {/* Adding by KIND rather than adding a blank one. The role decides how a
          rate behaves — a nightly band is layered narrower-wins, an arrangement
          competes on price — so it is the first thing to choose and the hardest
          to change afterwards. */}
      {onAdd && (
        <div className="row">
          <span className="note">{t('programme.addRule')}</span>
          {GROUPS.filter((g) => g.role !== 'other').map((g) => (
            <button
              key={g.role}
              type="button"
              className="btn"
              onClick={() => onAdd(g.role as RateRole)}
            >
              {t(g.heading)}
            </button>
          ))}
        </div>
      )}
      {find.trim() !== '' && [...grouped.values()].every((rows) => rows.length === 0) && (
        <p className="note">{t('programme.findNothing', { find })}</p>
      )}
      {GROUPS.map((group) => {
        const rows = grouped.get(group.role) ?? [];
        if (rows.length === 0) return null;
        return (
          <div className="rate-group" key={group.role}>
            <h3 className="eyebrow">
              {t(group.heading)}
              <span className="chip">{rows.length}</span>
            </h3>
            {/* One sentence saying what this kind of rate DOES. The names in a
                lifted card — "Lange laagseizoen tarief C" — say what the old
                system called them and nothing about how they behave. */}
            <p className="note">{t(group.note)}</p>
            {rows.map(({ rate: r, index: i }) => (
              <button
                type="button"
                key={r.id}
                className={`rt${selected === r.id ? ' on' : ''}`}
                onClick={() => onSelect(selected === r.id ? null : r.id)}
              >
                <span className="chip">{t('programme.rateNights', { nights: r.nights })}</span>
                <span>
                  <span className="nm">{r.label}</span>
                  <span className="meta">
                    {r.active.from} → {r.active.until}{' '}
                    {/* Each chip carries a sentence in its title. "EXTENDS" is
                        this system's word, not anybody else's. */}
                    {r.extend && (
                      <span className="chip" title={t('programme.extendsWhat')}>
                        {t('programme.extends')}
                      </span>
                    )}{' '}
                    {taperLabel(t, r) && (
                      <span className="chip tp" title={t('programme.taperWhat')}>
                        {taperLabel(t, r)}
                      </span>
                    )}{' '}
                    {inert.has(i) && (
                      <span className="chip bad" title={t('programme.inertWhat')}>
                        {t('programme.inert')}
                      </span>
                    )}{' '}
                    {r.requiresBookingTotalAtLeast && (
                      <span className="chip" title={t('programme.minTotalWhat')}>
                        {t('programme.minTotal')}
                      </span>
                    )}{' '}
                    {r.availability === 'closed' && (
                      <span className="chip bad" title={t('programme.blocksWhat')}>
                        {t('programme.blocks')}
                      </span>
                    )}
                  </span>
                </span>
                <span className="amt">{r.price?.base != null ? money(r.price.base) : '—'}</span>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One rate, with its MONEY editable.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PANEL IS IN TWO HALVES
 * ---------------------------------------------------------------------------
 *
 * This panel was read-only, and the only way to change any of it was the
 * "Programme source" textarea below — a JSON editor whose failure message is
 * "That is not valid JSON". For this park that was not a rough edge but the
 * whole job: 103 of 105 offers were identical between seasons and the two that
 * moved are long-stay ARRANGEMENTS, which the price grid does not cover because
 * they are not nightly bands. The single most important edit of the owner's year
 * needed a JSON editor.
 *
 * Money came first because it is what a new season is: the operator is copying
 * figures off a sheet, and the risk of typing one into the wrong box is that
 * they see the wrong number and fix it. Structure came second, in `RateShape`,
 * because a rate the park has never sold before is not a number — and leaving
 * that in the textarea meant "you may change your prices, but not your offers".
 *
 * The two are kept visibly apart. A wrong amount is loud; a wrong window is
 * silent until October, which is why the shape half opens with the whole rate
 * read back as a sentence.
 *
 * `onChange` receives the whole rate. The caller decides what saving means; this
 * component never writes.
 */
export function RateDetail({ rate, readOnly, periods, quantities, onChange }: {
  rate: Rate;
  /** A published version cannot be edited. Shows the values and no inputs. */
  readOnly?: boolean;
  /** The season's period library, so a window can be filled from it. */
  periods?: readonly Period[];
  /** The card's guest categories. One price row each, priced or not. */
  quantities?: readonly QuantityDef[];
  onChange?: (next: Rate) => void;
}) {
  const t = useT();
  const taper = rate.extend?.taper;
  const editable = !readOnly && typeof onChange === 'function';

  const setBase = (v: number | null) => {
    const price = { ...(rate.price ?? {}) };
    if (v === null) delete (price as { base?: number }).base;
    else (price as { base?: number }).base = v;
    onChange?.({ ...rate, price });
  };

  const setPerUnit = (key: string, v: number | null) => {
    const perUnit = { ...(rate.price?.perUnit ?? {}) };
    // Removing the KEY rather than setting zero: a surcharge of nothing and no
    // surcharge at all read the same to a guest and differently to the engine.
    if (v === null) delete perUnit[key];
    else perUnit[key] = v;
    const price = { ...(rate.price ?? {}), perUnit };
    if (Object.keys(perUnit).length === 0) delete (price as { perUnit?: unknown }).perUnit;
    onChange?.({ ...rate, price });
  };

  /**
   * Every guest category, priced or not, plus any orphan key the rate prices.
   *
   * The label is the operator's own word for the category rather than its key.
   * `q0` is what the lift called it; "Volwassene" is what they call it, and the
   * Guests screen is where that gets renamed.
   */
  const perUnitRows = (() => {
    const priced = rate.price?.perUnit ?? {};
    const rows = (quantities ?? []).map((q) => ({
      key: q.key,
      label: q.label || q.key,
      value: priced[q.key],
    }));
    const declared = new Set(rows.map((r) => r.key));
    for (const key of Object.keys(priced)) {
      if (!declared.has(key)) rows.push({ key, label: key, value: priced[key] });
    }
    return rows;
  })();

  return (
    <div className="scroll-x">
      <table>
        <tbody>


          <Money
            id={`amt-${rate.id}-base`}
            label={t('programme.basePrice')}
            value={rate.price?.base}
            editable={editable}
            onSet={setBase}
          />
          {/*
            A ROW PER GUEST CATEGORY THE CARD DECLARES, not per category that
            happens to have a price today.

            DEFECT (reported by the operator): clearing the price on q0 or q1
            made the row disappear, with no way to get it back short of editing
            the JSON. Which is exactly right at the storage layer — a surcharge
            of nothing and no surcharge at all are different things to the
            engine, so clearing must DELETE the key — and exactly wrong on
            screen, where deleting the only control that could undo it turns a
            cleared field into a one-way door.

            So the rows come from `quantities`. An empty one means the category
            is not charged for; typing in it charges again. Any key priced but
            not declared is listed too, or it would be uneditable for the
            opposite reason.
          */}
          {perUnitRows.map(({ key, label, value }) => (
            <Money
              key={key}
              id={`amt-${rate.id}-per-${key}`}
              label={t('programme.perUnit', { unit: label })}
              value={value}
              editable={editable}
              onSet={(next) => setPerUnit(key, next)}
            />
          ))}
          {rate.extend && (
            <>
              {/* The extra night's price is money and is editable; how many
                  nights a step covers is structure and is not. */}
              <Money
                id={`amt-${rate.id}-step`}
                editable={editable}
                label={t('programme.extensionStepPrice', { nights: rate.extend.stepNights })}
                value={rate.extend.stepPrice?.base}
                onSet={(v) => {
                  const extend = { ...rate.extend! };
                  const stepPrice = { ...(extend.stepPrice ?? {}) };
                  if (v === null) delete (stepPrice as { base?: number }).base;
                  else (stepPrice as { base?: number }).base = v;
                  onChange?.({ ...rate, extend: { ...extend, stepPrice } });
                }}
              />

            </>
          )}
          {taper && taper.mode !== 'none' && (
            <>

              <Money
                  id={`amt-${rate.id}-floor`}
                  editable={editable}
                  label={t('programme.convergesTo')}
                  value={taper.floorPerNight}
                  onSet={(v) => {
                    const t2 = { ...taper } as Record<string, unknown>;
                    if (v === null) delete t2.floorPerNight; else t2.floorPerNight = v;
                    onChange?.({
                      ...rate,
                      extend: { ...rate.extend!, taper: t2 as typeof taper },
                    });
                  }}
              />
            </>
          )}
          {rate.requiresBookingTotalAtLeast?.base != null && (
            <Money
              id={`amt-${rate.id}-min-total`}
              editable={editable}
              label={t('programme.bookingTotal')}
              value={rate.requiresBookingTotalAtLeast.base}
              onSet={(v) => {
                if (v === null) {
                  const next = { ...rate };
                  delete (next as { requiresBookingTotalAtLeast?: unknown })
                    .requiresBookingTotalAtLeast;
                  onChange?.(next);
                  return;
                }
                onChange?.({
                  ...rate,
                  requiresBookingTotalAtLeast: {
                    ...(rate.requiresBookingTotalAtLeast ?? {}), base: v,
                  },
                });
              }}
            />
          )}


        </tbody>
      </table>
      {/* The name, which was not editable at all. On a lifted card it is what
          the old system called the rule — "Lange laagseizoen tarief C" — and an
          operator re-entering a season has no way to write what it now is. */}
      <div className="row">
        <label className="grow">
          {t('programme.name')}
          <input
            type="text"
            value={rate.label ?? ''}
            disabled={readOnly}
            maxLength={120}
            onChange={(e) => onChange?.({ ...rate, label: e.target.value })}
          />
        </label>
      </div>
      <h4 className="eyebrow shape-heading">{t('programme.shapeHeading')}</h4>
      <p className="note">{t('programme.shapeNote')}</p>
      <RateShape rate={rate} readOnly={!editable} periods={periods} onChange={onChange} />
    </div>
  );
}

/**
 * One amount, typed in.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS AT MODULE SCOPE AND NOT INSIDE `RateDetail`
 * ---------------------------------------------------------------------------
 *
 * It was defined inside `RateDetail`'s body, which meant a NEW component type on
 * every render. React compares element types by identity, so every keystroke
 * unmounted the input and mounted a different one — the field lost focus after
 * one character, and typing `1150` was impossible. Only a paste worked, because
 * a paste is a single input event.
 *
 * Nothing about it looked wrong. It rendered, it saved, and it was unusable.
 *
 * ---------------------------------------------------------------------------
 * WHY IT HOLDS TEXT AND NOT A NUMBER
 * ---------------------------------------------------------------------------
 *
 * The same reason the price grid does. Binding the input to the parsed number
 * means a keystroke that does not complete a number cannot survive a render: type
 * `32`, then `,`, and the separator is parsed away and rendered gone — so the
 * next digit lands against `32` and produces `325`. A park that prices in cents
 * could not type its own prices.
 *
 * `,` and `.` are both accepted. A Dutch operator types a comma, a spreadsheet
 * pastes a point, and refusing either is refusing the way somebody works.
 */
function Money({ id, label, value, editable, onSet }: {
  id: string;
  label: string;
  value: number | null | undefined;
  editable: boolean;
  onSet: (v: number | null) => void;
}) {
  /** Null while not being typed in, so the stored value shows formatted. */
  const [text, setText] = useState<string | null>(null);
  /**
   * The last value THIS field asked for.
   *
   * When `value` arrives as something else, the change came from outside — an
   * Undo, a reload, another screen — and the half-typed text has been overruled
   * and must go. Without this, pressing Undo left `9999` sitting in a field whose
   * stored value was back to 1075, and the next save would have written it.
   */
  const committed = useRef<number | null | undefined>(value);

  useEffect(() => {
    if (value !== committed.current) { committed.current = value; setText(null); }
  }, [value]);

  if (!editable) return <Row k={label} v={value != null ? money(value) : '—'} />;

  const commit = (raw: string) => {
    const cleaned = raw.replace(/[^\d.,-]/g, '').replace(',', '.');
    if (cleaned === '') { committed.current = null; onSet(null); return; }
    const n = Number(cleaned);
    if (Number.isFinite(n) && n >= 0) { committed.current = n; onSet(n); }
  };

  return (
    <tr>
      <th style={{ width: 190 }}><label htmlFor={id}>{label}</label></th>
      <td>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          className="amount"
          value={text ?? (value ?? '')}
          onFocus={() => setText(value == null ? '' : String(value))}
          onChange={(e) => { setText(e.target.value); commit(e.target.value); }}
          onBlur={() => { setText(null); }}
        />
      </td>
    </tr>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <tr><th style={{ width: 190 }}>{k}</th><td>{v}</td></tr>;
}

export function ProgrammeEditor({ accommodationKey, programme, onApply, onRevert, dirty }: {
  /**
   * Identifies the ACCOMMODATION, not the programme.
   *
   * Reseeding on `programme.id` looked equivalent and was not: applying an edit
   * that changes the id is still the same accommodation, and treating it as a
   * switch wiped the "Applied — moved the arrival to …" message the operator
   * needs to read. The accommodation key is stable across edits, which is
   * exactly the property wanted here.
   */
  accommodationKey: string;
  programme: Programme;
  onApply: (p: Programme) => string | null;
  onRevert: () => void;
  dirty: boolean;
}) {
  const t = useT();
  const [text, setText] = useState(() => JSON.stringify(programme, null, 2));
  const [note, setNote] = useState('');
  const [key, setKey] = useState(accommodationKey);

  // Re-seed the textarea when the caller switches accommodation.
  if (key !== accommodationKey) {
    setKey(accommodationKey);
    setText(JSON.stringify(programme, null, 2));
    setNote('');
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="btn pri"
          onClick={() => {
            let parsed: Programme;
            try {
              parsed = JSON.parse(text) as Programme;
            } catch (err) {
              // The parser's own message, which the browser writes and this app
              // cannot translate, inside a sentence that it can.
              setNote(t('programme.invalidJson', { message: (err as Error).message }));
              return;
            }
            setNote(onApply(parsed) ?? t('programme.applied'));
          }}
        >
          {t('programme.applyChanges')}
        </button>
        <button type="button" className="btn" onClick={() => { onRevert(); setNote(''); }}>
          {t('programme.revert')}
        </button>
        <span className={`pill${dirty ? ' warn' : ''}`}>
          {dirty ? t('programme.edited') : t('programme.unchanged')}
        </span>
        <span className="note">{note}</span>
      </div>
      <textarea
        spellCheck={false}
        aria-label={t('programme.json')}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
    </>
  );
}
